import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * NotificationsFilled Icon component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconNotificationsFilled extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M6.50912 3.34956C7.94943 1.90926 9.90291 1.1001 11.9398 1.1001C13.9767 1.1001 15.9302 1.90925 17.3705 3.34956C18.8108 4.78987 19.62 6.74335 19.62 8.78026V15.6375C19.62 16.118 19.8109 16.5789 20.1507 16.9187C20.5362 17.3042 20.8392 17.4493 21.2629 17.4493C21.6919 17.4493 22.0398 17.7971 22.0398 18.2262C22.0398 18.6553 21.6919 19.0032 21.2629 19.0032H2.61677C2.18768 19.0032 1.83984 18.6553 1.83984 18.2262C1.83984 17.7971 2.18768 17.4493 2.61677 17.4493C3.0405 17.4493 3.34344 17.3042 3.72899 16.9187C4.06877 16.5789 4.25965 16.118 4.25965 15.6375V8.78026C4.25965 6.74335 5.06881 4.78987 6.50912 3.34956ZM9.22061 21.6901C9.22061 21.0465 9.74238 20.5247 10.386 20.5247H13.4937C14.1373 20.5247 14.6591 21.0465 14.6591 21.6901C14.6591 22.3337 14.1373 22.8555 13.4937 22.8555H10.386C9.74238 22.8555 9.22061 22.3337 9.22061 21.6901Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-notifications-filled') === undefined) {
  customElements.define('cat-icon-notifications-filled', BlocksIconNotificationsFilled);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-notifications-filled': BlocksIconNotificationsFilled;
  }
}
